import uiI18n from "../i18n";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LuBell,
  LuChevronDown,
  LuClock,
  LuEye,
  LuHeart,
  LuMenu,
  LuMessagesSquare,
  LuPlus,
  LuSearch,
  LuX,
} from "react-icons/lu";
import { useTranslation } from "react-i18next";

import PhoneHeader from "./PhoneHeader";
import usePhoneLayout from "../hooks/usePhoneLayout";
import UserMenu from "../components/UserMenu";
import LanguageSelector from "../components/LanguageSelector";
import HeaderLogo from "./HeaderLogo";
import HeaderMenu from "./HeaderMenu";
import HeaderMobileMenu from "./HeaderMobileMenu";
import { useMessageContext } from "../context/MessageContext";

export default function Header() {
  const isPhone = usePhoneLayout();
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth < 1280
  );
  const [addAdMenuOpen, setAddAdMenuOpen] = useState(false);

  const addAdMenuRef = useRef(null);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const headerRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    setMenuOpen(false);
    setAccountOpen(false);
  }, [location]);
  const { t } = useTranslation();

  const {
    newIncomingMessage,
    setNewIncomingMessage,
  } = useMessageContext();

  // ---------------------------------------------------------
  // Responsive
  // ---------------------------------------------------------
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1280;

      setIsMobile(mobile);

      if (!mobile) {
        setMenuOpen(false);
        setAccountOpen(false);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // ---------------------------------------------------------
  // Fermeture menus : clic extérieur + Escape
  // ---------------------------------------------------------
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          !toggleRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
      if (
        addAdMenuRef.current &&
        !addAdMenuRef.current.contains(event.target)
      ) {
        setAddAdMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setAddAdMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener(
        "pointerdown",
        handleOutsideClick
      );

      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("lina-mobile-menu-open", isMobile && (menuOpen || accountOpen));
    return () => document.body.classList.remove("lina-mobile-menu-open");
  }, [isMobile, menuOpen, accountOpen]);

  // ---------------------------------------------------------
  // Bloquer le scroll quand menu mobile ouvert
  // ---------------------------------------------------------
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;

    if (menuOpen && isMobile) {
      // Clip avoids creating a body scroll container that would unstick the header.
      document.body.style.overflow = "clip";
      document.documentElement.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
    };
  }, [menuOpen, isMobile]);

  useEffect(() => {
    if (!isMobile || !headerRef.current) return;
    const header = headerRef.current;
    const container = header.parentElement;
    const updateHeight = () => container.style.setProperty(
      "--mobile-header-height", `${header.getBoundingClientRect().height}px`
    );
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(header);
    return () => {
      observer.disconnect();
      container.style.removeProperty("--mobile-header-height");
    };
  }, [isMobile, isPhone]);

  // ---------------------------------------------------------
  // Focus trap mobile
  // ---------------------------------------------------------
  useEffect(() => {
    if (!menuOpen || !isMobile) return;

    const panel = menuRef.current;
    const toggleButton = toggleRef.current;

    const first = panel?.querySelector("button");

    first?.focus({ preventScroll: true });

    const trapFocus = (event) => {
      const buttons = [
        ...panel.querySelectorAll("button:not([disabled])"),
      ];

      if (!buttons.length) return;

      const firstButton = buttons[0];
      const lastButton = buttons[buttons.length - 1];

      if (
        event.key === "Tab" &&
        event.shiftKey &&
        document.activeElement === firstButton
      ) {
        event.preventDefault();
        lastButton?.focus();
      }

      if (
        event.key === "Tab" &&
        !event.shiftKey &&
        document.activeElement === lastButton
      ) {
        event.preventDefault();
        firstButton?.focus();
      }
    };

    panel?.addEventListener("keydown", trapFocus);

    return () => {
      panel?.removeEventListener("keydown", trapFocus);
      toggleButton?.focus({ preventScroll: true });
    };
  }, [menuOpen, isMobile]);

  // ---------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------
  const toggleMenu = () => {
    setAccountOpen(false);
    setMenuOpen((previousValue) => !previousValue);
    setAddAdMenuOpen(false);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);

    setMenuOpen(false);
    setAddAdMenuOpen(false);

    if (path === "/ConversationPage") {
      setNewIncomingMessage(null);
    }
  };

  const handleCreateAdClick = (path) => {
    handleMenuItemClick(path);
  };

  // ---------------------------------------------------------
  // Les 3 fonctionnalités Engagement
  // ---------------------------------------------------------
  const quickActions = [
    {
      label: t("recent_searches") || uiI18n.t("recent_searches"),
      icon: LuBell,
      path: "/recent-searches",
    },
    {
      label: t("my_favorites") || "Favoris",
      icon: LuHeart,
      path: "/favorites",
    },
    {
      label: t("recently_viewed") || "Consultés",
      icon: LuEye,
      path: "/recently-viewed",
    },
  ];
 
  if (isPhone) return <PhoneHeader {...{ menuOpen, accountOpen, setAccountOpen, setMenuOpen, toggleMenu,
    toggleRef, menuRef, addAdMenuOpen, setAddAdMenuOpen, handleMenuItemClick, handleCreateAdClick, newIncomingMessage }} />;
  return (
  <>
  {/* Desktop : bandeau secondaire (non collant, il défile avec la page). Mêmes routes et même sélecteur de langue qu'avant. */}
  {!isMobile && (
    <div className="lina-topbar">
      <div className="lina-topbar-inner">
        <p className="lina-topbar-tagline">{uiI18n.t("ui.real_estate_made_simple")}</p>
        <div className="lina-topbar-actions">
          <button type="button" onClick={() => handleMenuItemClick("/recent-searches")} className="lina-topbar-link">
            <LuClock size={15} aria-hidden="true" />
            <span>{t("recent_searches")}</span>
          </button>
          <button type="button" onClick={() => handleMenuItemClick("/recently-viewed")} className="lina-topbar-link">
            <LuEye size={15} aria-hidden="true" />
            <span>{t("recently_viewed")}</span>
          </button>
          <LanguageSelector variant="bar" />
        </div>
      </div>
    </div>
  )}
  <div className="sticky top-0 z-50 border-b border-slate-200 bg-white">
    <header ref={headerRef} className="mx-auto min-h-[64px] w-full max-w-[1600px] px-3 sm:px-4 lg:px-6 xl:min-h-[76px]">

      {/* =====================================================
          MOBILE / TABLETTE (< 1280 px)
          Hamburger + logo, puis (recherche à partir de 480 px), favoris, messages et compte.
          La langue est sur une ligne dédiée juste sous le header.
          ===================================================== */}
      {isMobile ? (
        <div className="lina-m-header">
          <div className="lina-m-header-start">
            <button
              ref={toggleRef}
              type="button"
              onClick={toggleMenu}
              aria-label={
                menuOpen
                  ? uiI18n.t("ui.close_menu")
                  : uiI18n.t("ui.open_menu")
              }
              aria-expanded={menuOpen}
              aria-controls="lina-mobile-menu"
              className="lina-m-icon lina-m-menu-button"
            >
              {menuOpen ? <LuX size={22} /> : <LuMenu size={22} />}
            </button>

            <HeaderLogo navigate={navigate} />
          </div>

          <div className="lina-m-header-actions">
            {/* Messages (avec la pastille de notification des nouveaux messages) */}
            <button
              type="button"
              onClick={() => handleMenuItemClick("/ConversationPage")}
              aria-label={t("messages")}
              title={t("messages")}
              className="lina-m-icon"
            >
              <LuMessagesSquare size={20} strokeWidth={1.9} />
              {newIncomingMessage && (
                <>
                  <span className="absolute end-1.5 top-1.5 h-2.5 w-2.5 animate-ping rounded-full bg-red-500 opacity-60" />
                  <span className="absolute end-1.5 top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-red-500" />
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => handleMenuItemClick("/favorites")}
              aria-label={t("mobile.favorites")}
              title={t("mobile.favorites")}
              className="lina-m-icon"
            >
              <LuHeart size={20} strokeWidth={1.9} />
            </button>

            <div className="lina-m-header-account">
              <UserMenu open={accountOpen} onOpenChange={(open) => {
                setAccountOpen(open);
                if (open) setMenuOpen(false);
              }} />
            </div>
          </div>
        </div>
      ) : (
        /* =====================================================
           DESKTOP
           ===================================================== */
        <div className="lina-responsive-desktop flex min-h-[76px] w-full items-center gap-3">
          {/* Logo */}
          <HeaderLogo navigate={navigate} />

          {/* Navigation */}
          <HeaderMenu
            handleMenuItemClick={handleMenuItemClick}
          />

          {/* Actions */}
          <div className="lina-desktop-actions ms-auto flex shrink-0 items-center gap-1">

            {/* Je recherche un bien (demande de recherche) */}
            <button
              type="button"
              onClick={() => handleMenuItemClick("/property-request/new")}
              className="lina-desktop-cta flex h-11 items-center gap-2 whitespace-nowrap rounded-xl border-2 border-green-700 bg-white px-4 text-sm font-semibold text-green-800 transition hover:bg-green-50 focus:outline-none focus:ring-4 focus:ring-green-500/20"
            >
              <LuSearch size={16} aria-hidden="true" />
              <span>{t("propertyRequest.home.start")}</span>
            </button>

            {/* Déposer une annonce */}
            <div
              ref={addAdMenuRef}
              className="relative"
            >
              <button
                type="button"
                aria-expanded={addAdMenuOpen}
                aria-label={t("add_ad")}
                onClick={() =>
                  setAddAdMenuOpen(
                    (value) => !value
                  )
                }
                className="
                  lina-desktop-cta
                  flex h-11 items-center gap-2
                  whitespace-nowrap rounded-xl
                  bg-green-600 px-4
                  text-sm font-semibold text-white
                  shadow-sm transition
                  hover:bg-green-700
                  focus:outline-none
                  focus:ring-4
                  focus:ring-green-500/20
                "
              >
                <LuPlus size={18} />

                {/* Libellé court uniquement dans la zone compacte 1280–1535 px (design-system.css). */}
                <span className="lina-publish-full">{t("add_ad")}</span>
                <span className="lina-publish-short">{t("add_ad_short")}</span>

                <LuChevronDown
                  size={14}
                  className={`transition-transform ${
                    addAdMenuOpen
                      ? "rotate-180"
                      : ""
                  }`}
                />
              </button>

              {addAdMenuOpen && (
                <div
                  className="
                    absolute end-0 top-full z-[70]
                    mt-2 w-72 overflow-hidden
                    rounded-2xl
                    border border-slate-200
                    bg-white p-2 shadow-xl
                  "
                >
                  <button
                    type="button"
                    onClick={() =>
                      handleCreateAdClick(
                        "/CreateBien"
                      )
                    }
                    className="
                      block w-full rounded-xl
                      px-4 py-3 text-start
                      transition
                      hover:bg-slate-50
                    "
                  >
                    <span className="block text-sm font-semibold text-slate-900">
                      {t("create_ad")}
                    </span>

                    <small className="mt-1 block text-xs text-slate-500">
                      {uiI18n.t(
                        "ui.sale_or_long_term_rental"
                      )}
                    </small>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      handleCreateAdClick(
                        "/CreateVacation"
                      )
                    }
                    className="
                      block w-full rounded-xl
                      px-4 py-3 text-start
                      transition
                      hover:bg-slate-50
                    "
                  >
                    <span className="block text-sm font-semibold text-slate-900">
                      {t("create_adW")}
                    </span>

                    <small className="mt-1 block text-xs text-slate-500">
                      {uiI18n.t(
                        "ui.daily_or_holiday_rental"
                      )}
                    </small>
                  </button>
                </div>
              )}
            </div>

            {/* Favoris (icône seule) */}
            <button
              type="button"
              onClick={() =>
                handleMenuItemClick(
                  "/favorites"
                )
              }
              className="lina-desktop-icon"
              aria-label={t("my_favorites")} title={t("my_favorites")}
            >
              <LuHeart
                size={22}
                strokeWidth={1.8}
                aria-hidden="true"
              />
            </button>

            {/* Messages (icône seule, avec la pastille des nouveaux messages) */}
            <button
              type="button"
              onClick={() =>
                handleMenuItemClick(
                  "/ConversationPage"
                )
              }
              className="lina-desktop-icon"
              aria-label={t("my_message") || uiI18n.t("messages")}
              title={
                t("my_message") ||
                uiI18n.t("messages")
              }
            >
              <span className="relative">
                <LuMessagesSquare
                  size={22}
                  strokeWidth={1.8}
                  aria-hidden="true"
                />

                {newIncomingMessage && (
                  <span className="absolute -end-1.5 -top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-red-500" />
                )}
              </span>
            </button>

            {/* Séparateur */}
            <div className="mx-1 h-9 w-px bg-slate-200" />

            {/* Profil */}
            <UserMenu />
          </div>
        </div>
      )}
    </header>

    {/* Menu hamburger mobile */}
    {isMobile && menuOpen && (
      <HeaderMobileMenu
        toggleMenu={toggleMenu}
        handleMenuItemClick={
          handleMenuItemClick
        }
        addAdMenuOpen={addAdMenuOpen}
        setAddAdMenuOpen={
          setAddAdMenuOpen
        }
        handleCreateAdClick={
          handleCreateAdClick
        }
        menuRef={menuRef}
        newIncomingMessage={
          newIncomingMessage
        }
      />
    )}
  </div>

  {/* Mobile : langue sur une ligne dédiée sous le header (libère la barre pour messages, favoris, compte). */}
  {isMobile && (
    <div className="lina-m-langbar">
      <LanguageSelector variant="row" />
    </div>
  )}
  </>
);
} 
